import React, { useCallback, useMemo, useEffect } from 'react';
import {
  FilterOperator,
  FilterParams,
  FilterValue,
} from '@antimatterhq/ui/types/TableFilter';
import { SortDirection } from '@antimatterhq/ui/types/TableSort';
import {
  TableColumn,
  TableFilterInfo,
  TableSortInfo,
} from '@antimatterhq/ui/types/TableColumn';
import { useInitialTableColumnState } from '@antimatterhq/ui/hooks/table/useInitialTableColumnState';
import {
  TABLE_COLUMN_PINNED_STORAGE_KEY,
  TABLE_COLUMN_ORDER_STORAGE_KEY,
  TABLE_COLUMN_VISIBILITY_STORAGE_KEY,
  TABLE_COLUMN_WIDTH_STORAGE_KEY,
  TABLE_MAX_COLUMN_WIDTH,
  TABLE_MIN_COLUMN_WIDTH,
  TABLE_COLUMN_HEADER_ICON_WIDTH,
} from '@antimatterhq/ui/constants/table';
import { compareColumnPositions } from '@antimatterhq/ui/utils/table/compareColumnPositions';
import { useTableContext } from '@antimatterhq/ui/components/table';
import { useLocalStorageSyncedState } from '@antimatterhq/ui/hooks/state/useLocalStorageSyncedState';

export type UseTableColumnsOptions = {
  sortDirection?: SortDirection;
  // Sorting
  sortKey?: string | null;
  setSortBy?: (key: string) => void;
  // Filters
  filters?: FilterParams;
  setFilter?: (
    id: string,
    value: FilterValue,
    operator?: FilterOperator,
  ) => void;
  // Persistence
  persistState?: boolean;
};

export function useTableColumns(opts?: UseTableColumnsOptions) {
  const { columnDefinitions, tableKey } = useTableContext();

  const {
    sortKey,
    sortDirection,
    setSortBy,
    setFilter,
    filters,
    persistState = true,
  } = opts ?? {};

  const {
    initialColumnPinned,
    initialColumnOrders,
    initialColumnVisibility,
    initialColumnWidths,
  } = useInitialTableColumnState(columnDefinitions, tableKey, persistState);

  // Pinned columns
  const [pinnedColumns, setPinnedColumns] = useLocalStorageSyncedState(
    tableKey,
    TABLE_COLUMN_PINNED_STORAGE_KEY,
    initialColumnPinned,
    persistState,
  );

  // ---------------------------------------------------------------------------
  //  Dynamic minimum width calculation
  // ---------------------------------------------------------------------------

  const CHAR_WIDTH_ESTIMATE = 8; // px – rough average for body font
  const PADDING_X = 12; // total horizontal padding inside th

  const minWidthsRef = React.useRef<Record<string, number>>({});

  const computeMinWidth = useCallback(
    (
      colDef: (typeof columnDefinitions)[number],
      minWidthOpts: { sort: boolean; filter: boolean; resize: boolean },
    ): number => {
      const textWidth =
        typeof colDef.title === 'string'
          ? colDef.title.length * CHAR_WIDTH_ESTIMATE
          : 80; // fallback

      const iconCount =
        (minWidthOpts.sort ? 1 : 0) +
        (minWidthOpts.filter ? 1 : 0) +
        (minWidthOpts.resize ? 1 : 0);

      const iconsWidth = iconCount * (TABLE_COLUMN_HEADER_ICON_WIDTH + 4);

      return Math.max(
        TABLE_MIN_COLUMN_WIDTH,
        textWidth + iconsWidth + PADDING_X,
      );
    },
    [],
  );

  // Column orders
  const [columnOrders, setColumnOrders] = useLocalStorageSyncedState(
    tableKey,
    TABLE_COLUMN_ORDER_STORAGE_KEY,
    initialColumnOrders,
    persistState,
  );

  // Column visibility
  const [columnVisibility, setColumnVisibility] = useLocalStorageSyncedState(
    tableKey,
    TABLE_COLUMN_VISIBILITY_STORAGE_KEY,
    initialColumnVisibility,
    persistState,
  );

  // Column widths
  const [columnWidths, setColumnWidths] = useLocalStorageSyncedState(
    tableKey,
    TABLE_COLUMN_WIDTH_STORAGE_KEY,
    initialColumnWidths,
    persistState,
  );

  // A ref that always has the *latest* committed widths (either from
  // React state or from the current drag session).  This lets us compute
  // offsets or other metrics without triggering re-renders.
  const liveColumnWidths =
    React.useRef<Record<string, number>>(initialColumnWidths);

  // Keep the ref in sync whenever state changes (e.g. visibility reset)
  useEffect(() => {
    liveColumnWidths.current = columnWidths;
  }, [columnWidths]);

  // Utility: set CSS custom property for width (and optional offset)
  const setCSSVar = useCallback(
    (id: string, width: number, offset?: number) => {
      const root = document.documentElement;
      root.style.setProperty(`--${tableKey}-${id}-width`, `${width}px`);
      if (offset !== undefined) {
        root.style.setProperty(`--${tableKey}-${id}-offset`, `${offset}px`);
      }
    },
    [tableKey],
  );

  /**
   * Update the CSS custom property that stores the table's overall width.
   *
   * The width should only include columns that are currently visible. Using
   * `liveColumnWidths` ensures we include in-progress drag resize values
   * without triggering a React render.
   */
  const updateTotalWidthVar = useCallback(() => {
    const total = Object.entries(liveColumnWidths.current)
      .filter(([id]) => columnVisibility[id])
      .reduce((acc, [, width]) => acc + width, 0);

    document.documentElement.style.setProperty(
      `--${tableKey}-total-width`,
      `${total}px`,
    );
  }, [tableKey, columnVisibility]);

  // Track sorted columns in a ref so we can compute pinned offsets during drag
  const sortedColumnsRef = React.useRef<typeof sortedColumns | null>(null);

  // Helper to recompute and push CSS variables for sticky offsets.
  const updatePinnedOffsets = useCallback(() => {
    const cols = sortedColumnsRef.current;
    if (!cols?.length) return;

    // Left-pinned pass (left to right)
    let offset = 0;
    for (const col of cols) {
      const pinSide = pinnedColumns[col.id];
      if (pinSide === 'left') {
        const width = liveColumnWidths.current[col.id] ?? 0;
        document.documentElement.style.setProperty(
          `--${tableKey}-${col.id}-offset`,
          `${offset}px`,
        );
        offset += width;
      }
    }

    // Right-pinned pass (right to left)
    offset = 0;
    for (let i = cols.length - 1; i >= 0; i--) {
      const col = cols[i];
      const pinSide = pinnedColumns[col.id];
      if (pinSide === 'right') {
        const width = liveColumnWidths.current[col.id] ?? 0;
        document.documentElement.style.setProperty(
          `--${tableKey}-${col.id}-offset`,
          `${offset}px`,
        );
        offset += width;
      }
    }
  }, [pinnedColumns, tableKey]);

  // Initialise CSS variables once when the hook mounts
  useEffect(() => {
    Object.entries(initialColumnWidths).forEach(([id, width]) =>
      setCSSVar(id, width),
    );
    updatePinnedOffsets();
    updateTotalWidthVar();
  }, [
    initialColumnWidths,
    setCSSVar,
    updatePinnedOffsets,
    updateTotalWidthVar,
  ]);

  // Recalculate the total width whenever the committed widths _or_ column
  // visibility changes.  This keeps the table's `min-width` CSS in sync when
  // users toggle columns on/off or programmatically adjust widths.
  useEffect(() => {
    updateTotalWidthVar();
  }, [columnWidths, columnVisibility, updateTotalWidthVar]);

  // ---------------------------------------------
  // Resize performance helpers
  // ---------------------------------------------
  // We buffer live width changes in a ref so that we can coalesce many
  // `mousemove` events into a single React state update per animation
  // frame.  This keeps the UI buttery-smooth while avoiding the heavier
  // re-render cost of calling `setState` dozens of times per second.

  // Holds the *next* widths that need to be flushed to React state.
  const pendingWidthUpdates = React.useRef<Record<string, number>>({});

  // Flush all pending widths to React state (called on mouseup)
  const commitPendingWidths = useCallback(() => {
    if (Object.keys(pendingWidthUpdates.current).length === 0) return;

    setColumnWidths((prev) => ({ ...prev, ...pendingWidthUpdates.current }));
    liveColumnWidths.current = {
      ...liveColumnWidths.current,
      ...pendingWidthUpdates.current,
    };
    updateTotalWidthVar();
    pendingWidthUpdates.current = {};
  }, [setColumnWidths, updateTotalWidthVar]);

  // Resize Handlers
  const handleColumnResize = useCallback(
    (resizeKey: string, width: number) => {
      const minWidth =
        minWidthsRef.current[resizeKey] ?? TABLE_MIN_COLUMN_WIDTH;

      const clampedWidth = Math.max(
        minWidth,
        Math.min(width, TABLE_MAX_COLUMN_WIDTH),
      );

      // Update CSS variable for immediate visual feedback.
      setCSSVar(resizeKey, clampedWidth);

      // Store in pending map so we can commit later on mouseup.
      pendingWidthUpdates.current[resizeKey] = clampedWidth;

      // Keep our live ref in sync for calculations during drag.
      liveColumnWidths.current[resizeKey] = clampedWidth;

      // Update pinned offsets since widths changed.
      updatePinnedOffsets();

      // Update total width CSS variable for min-width enforcement.
      updateTotalWidthVar();
    },
    [setCSSVar, updatePinnedOffsets, updateTotalWidthVar],
  );

  // Column Visibility Handlers
  const toggleColumnVisibility = useCallback(
    (id: string) => {
      setColumnVisibility((prev) => {
        // Get the current visibility state
        const isCurrentlyVisible = prev[id];

        // If trying to hide a column
        if (isCurrentlyVisible) {
          // Check if this is a pinned column
          const isPinned = pinnedColumns[id];
          if (isPinned) {
            return prev; // Don't allow hiding pinned columns
          }

          // Count how many columns would remain visible
          const visibleColumnsCount = Object.entries(prev).filter(
            ([colId, isVisible]) =>
              isVisible && colId !== id && !pinnedColumns[colId],
          ).length;

          // Don't allow hiding if this is the last visible non-pinned column
          if (visibleColumnsCount === 0) {
            return prev;
          }
        }

        // Toggle the visibility
        return { ...prev, [id]: !prev[id] };
      });
    },
    [setColumnVisibility, pinnedColumns],
  );

  // Column Order Handlers
  const onColumnOrderChange = useCallback(
    (_id: string, oldIndex: number, newIndex: number) => {
      setColumnOrders((prev) => {
        // Build an ordered list of *unpinned* columns based on the current
        // positions.  Pinned columns are excluded from the `columnOrders`
        // map, so we can safely operate on this list without worrying about
        // left / right sticky columns.
        const orderedColumnIds = Object.keys(prev)
          .map((colId) => ({ id: colId }))
          .sort((a, b) => compareColumnPositions(a, b, pinnedColumns, prev))
          .map((item) => item.id);

        // Sanity-check indexes – if they are out of bounds just bail.
        if (
          oldIndex < 0 ||
          newIndex < 0 ||
          oldIndex >= orderedColumnIds.length ||
          newIndex >= orderedColumnIds.length
        ) {
          return prev;
        }

        // Move the dragged column ID within the ordered list.
        const updatedOrder = [...orderedColumnIds];
        const [moved] = updatedOrder.splice(oldIndex, 1);
        updatedOrder.splice(newIndex, 0, moved);

        // Re-create the order mapping so that every column receives the
        // correct sequential index (starting from 0).  This guarantees that
        // indices remain unique and densely packed which simplifies future
        // comparisons.
        const newOrders: Record<string, number> = {};
        updatedOrder.forEach((colId, index) => {
          newOrders[colId] = index;
        });

        return newOrders;
      });
    },
    [setColumnOrders, pinnedColumns],
  );

  // Batch update column orders - much more efficient for sorting
  const setAllColumnOrders = useCallback(
    (orderedColumnIds: string[]) => {
      setColumnOrders((prev) => {
        // Start with the list supplied by the caller, then tack on any
        // remaining columns (e.g. hidden ones) in their existing order so
        // we don’t accidentally drop them.
        const remainingIds = Object.keys(prev).filter(
          (id) => !orderedColumnIds.includes(id),
        );

        const fullOrder = [...orderedColumnIds, ...remainingIds];

        const newOrders: Record<string, number> = {};
        fullOrder.forEach((id, idx) => {
          newOrders[id] = idx;
        });

        return newOrders;
      });
    },
    [setColumnOrders],
  );

  // TODO: Implement this if we need to toggle column pinning
  const toggleColumnPin = useCallback(() => {}, []);

  // Reset Handlers
  const resetColumnVisibility = useCallback(() => {
    setColumnVisibility(
      columnDefinitions.reduce(
        (acc, col) => ({ ...acc, [col.id]: !col.hidden }),
        {} as Record<string, boolean>,
      ),
    );
  }, [columnDefinitions, setColumnVisibility]);

  const resetColumnWidths = useCallback(() => {
    setColumnWidths(
      columnDefinitions.reduce(
        (acc, col) => {
          const filterEnabled =
            col.enableFiltering === true || col.enableFiltering === undefined;
          const sortEnabled =
            col.enableSorting === true || col.enableSorting === undefined;
          const resizeEnabled =
            col.enableResize === true || col.enableResize === undefined;

          const dynamicMin = computeMinWidth(col, {
            sort: sortEnabled && !!setSortBy,
            filter: filterEnabled && !!setFilter,
            resize: resizeEnabled,
          });

          return {
            ...acc,
            [col.id]: Math.max(col.width ?? dynamicMin, dynamicMin),
          };
        },
        {} as Record<string, number>,
      ),
    );
  }, [
    columnDefinitions,
    computeMinWidth,
    setColumnWidths,
    setFilter,
    setSortBy,
  ]);

  const resetColumnOrders = useCallback(() => {
    setColumnOrders(
      columnDefinitions
        .filter((col) => !col.pinned)
        .reduce(
          (acc, col, index) => ({ ...acc, [col.id]: col.order ?? index }),
          {} as Record<string, number>,
        ),
    );
  }, [columnDefinitions, setColumnOrders]);

  const resetPinnedColumns = useCallback(() => {
    setPinnedColumns(
      columnDefinitions.reduce(
        (acc, col) => ({ ...acc, [col.id]: col.pinned }),
        {} as Record<string, 'left' | 'right' | undefined>,
      ),
    );
  }, [columnDefinitions, setPinnedColumns]);

  const resetAllColumns = useCallback(() => {
    resetColumnWidths();
    resetColumnVisibility();
    resetColumnOrders();
    resetPinnedColumns();
  }, [
    resetColumnWidths,
    resetColumnVisibility,
    resetColumnOrders,
    resetPinnedColumns,
  ]);

  // Filtered and sorted columns based on order, visibility, and pinned columns
  const filteredColumns = useMemo(() => {
    return columnDefinitions.filter(
      (columnDefinition) => columnVisibility[columnDefinition.id as string],
    );
  }, [columnDefinitions, columnVisibility]);

  const sortedColumns = useMemo(() => {
    return [...filteredColumns].sort((a, b) =>
      compareColumnPositions(a, b, pinnedColumns, columnOrders),
    );
  }, [filteredColumns, pinnedColumns, columnOrders]);

  // Keep ref in sync with latest sortedColumns
  useEffect(() => {
    sortedColumnsRef.current = sortedColumns;
  }, [sortedColumns]);

  // Get column styles
  const getStylesForColumn = useCallback(
    (columnId: string) => {
      const isPinned = pinnedColumns[columnId];

      const widthVar = `var(--${tableKey}-${columnId}-width)`;

      const base: React.CSSProperties = {
        width: widthVar,
        maxWidth: widthVar,
        flex: `0 0 ${widthVar}`,
      };

      if (!isPinned) return base;

      const offsetVar = `var(--${tableKey}-${columnId}-offset)`;

      return {
        ...base,
        position: 'sticky',
        [isPinned]: offsetVar,
        marginLeft: isPinned === 'right' ? 'auto' : undefined,
        zIndex: 1,
      } as React.CSSProperties;
    },
    [pinnedColumns, tableKey],
  );

  const getCellWidth = useCallback(
    (columnId: string) => {
      return columnWidths[columnId as string];
    },
    [columnWidths],
  );

  const columns = useMemo(() => {
    return sortedColumns.map((columnDefinition) => {
      const columnFilterInfo = filters?.[columnDefinition.id];

      const filterEnabled =
        columnDefinition.enableFiltering === true ||
        columnDefinition.enableFiltering === undefined;

      const sortEnabled =
        columnDefinition.enableSorting === true ||
        columnDefinition.enableSorting === undefined;

      const resizeEnabled =
        columnDefinition.enableResize === true ||
        columnDefinition.enableResize === undefined;

      // Calculate and cache dynamic min width for this column
      const minWidth = computeMinWidth(columnDefinition, {
        sort: sortEnabled && !!setSortBy,
        filter: filterEnabled && !!setFilter,
        resize: resizeEnabled,
      });

      if (minWidthsRef.current[columnDefinition.id] !== minWidth) {
        minWidthsRef.current[columnDefinition.id] = minWidth;
      }

      if (filterEnabled && setFilter && !filters) {
        console.warn(
          'setFilter is enabled but no filters are provided. Please provide filters to the useTableColumns hook.',
        );
      }

      const filterInfo: TableFilterInfo =
        filterEnabled && setFilter
          ? {
              setFilter: (value: FilterValue, operator?: FilterOperator) =>
                setFilter(columnDefinition.id, value, operator),
              filterValue: columnFilterInfo?.value,
              filterOperator: columnFilterInfo?.operator,
            }
          : {};

      const sortInfo: TableSortInfo =
        sortEnabled && setSortBy
          ? {
              setSortBy: () => setSortBy(columnDefinition.id),
              sortDirection:
                sortKey === columnDefinition.id ? sortDirection : undefined,
            }
          : {};

      return {
        id: columnDefinition.id,
        title: columnDefinition.title,
        tooltip: columnDefinition.tooltip !== false,
        handleResize: resizeEnabled
          ? (width: number) => handleColumnResize(columnDefinition.id, width)
          : undefined,
        getCellStyles: () => getStylesForColumn(columnDefinition.id),
        getCellWidth: () => getCellWidth(columnDefinition.id),
        finalizeResize: commitPendingWidths,
        ...sortInfo,
        ...filterInfo,
      } as TableColumn;
    });
  }, [
    sortedColumns,
    filters,
    computeMinWidth,
    setSortBy,
    setFilter,
    sortKey,
    sortDirection,
    commitPendingWidths,
    handleColumnResize,
    getStylesForColumn,
    getCellWidth,
  ]);

  return {
    columns,
    columnOrders,
    columnVisibility,
    columnWidths,
    pinnedColumns,
    onColumnOrderChange,
    setAllColumnOrders,
    resetAllColumns,
    resetColumnWidths,
    toggleColumnPin,
    toggleColumnVisibility,
    finalizeResize: commitPendingWidths,
  };
}
